import { forwardRef } from 'react';
import { useTheme } from 'styled-components';
import type {
  BorderProps,
  ResponsiveValue,
  ShadowProps,
  TypographyProps,
} from 'styled-system';
import { border, shadow, typography } from 'styled-system';

import type {
  BorderOverrideProps,
  CursorProps,
  TextDecorationProps,
  TextRenderingProps,
  TextTransformProps,
  UserSelectProps,
  WhiteSpaceProps,
} from '../extensions';
import {
  borderShorthandOverride,
  cursor,
  textDecoration,
  textRendering,
  textTransform,
  userSelect,
  whiteSpace,
} from '../extensions';
import styledFactory from '../styledFactory';
import type { ThemedButton } from '../theme';
import variantFactory from '../variantFactory';

type Size = ResponsiveValue<string>;
type Palette = ResponsiveValue<string>;
type Variant = ResponsiveValue<string>;

export interface SystemButtonProps
  extends Omit<BorderProps, keyof BorderOverrideProps>,
    BorderOverrideProps,
    CursorProps,
    ShadowProps,
    TextDecorationProps,
    TextTransformProps,
    TextRenderingProps,
    TypographyProps,
    UserSelectProps,
    WhiteSpaceProps {
  __buttonCommon__?: 'common';
  __buttonSquareCommon__?: 'common';
  __buttonSquareSize__?: Size;
  __buttonRoundedCommon__?: 'common';
  __buttonRoundedSize__?: Size;
  __buttonRoundCommon__?: 'common';
  __buttonRoundSize__?: Size;
  __buttonOtherCommon__?: 'common';
  __buttonOtherSize__?: Size;
  palette?: Palette;
  disabledVariant?: Variant;
  variant?: Variant;
}

const SystemButton = styledFactory<SystemButtonProps, 'button'>(
  'button',
  'Button',
  [
    variantFactory('components.button', { prop: '__buttonCommon__' }),
    variantFactory('components.button.shape.square', {
      prop: '__buttonSquareCommon__',
    }),
    variantFactory('components.button.shape.square.variants', {
      prop: '__buttonSquareSize__',
    }),
    variantFactory('components.button.shape.rounded', {
      prop: '__buttonRoundedCommon__',
    }),
    variantFactory('components.button.shape.rounded.variants', {
      prop: '__buttonRoundedSize__',
    }),
    variantFactory('components.button.shape.round', {
      prop: '__buttonRoundCommon__',
    }),
    variantFactory('components.button.shape.round.variants', {
      prop: '__buttonRoundSize__',
    }),
    variantFactory('components.button.shape.other', {
      prop: '__buttonOtherCommon__',
    }),
    variantFactory('components.button.shape.other.variants', {
      prop: '__buttonOtherSize__',
    }),
    variantFactory('components.button.palette', { prop: 'palette' }),
    variantFactory('components.button.disabled', { prop: 'disabledVariant' }),
    variantFactory('components.button.variants'),
  ],
  [
    border,
    cursor,
    shadow,
    borderShorthandOverride,
    textDecoration,
    textRendering,
    textTransform,
    typography,
    userSelect,
    whiteSpace,
  ],
);

export type Props = {
  shape?: 'square' | 'round' | 'rounded' | 'other';
  size?: Size;
  bc?: string;
  href?: string;
} & React.ComponentPropsWithRef<typeof SystemButton>;

/**
 * Button is a component that renders a styled `<button/>` or `<a/>` element.
 * An `<a/>` element is rendered if the `href` prop is set.
 */
const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, Props>(
  (props_, ref) => {
    const buttonTheme: ThemedButton | undefined = useTheme().components.button;

    let props = { ...props_ };

    let defaults: Props = {
      variant: 'default',
      disabledVariant: 'default',
    };

    if (buttonTheme?.bcPropInterop === 'variants') {
      if (props.shape != null) {
        throw new Error(
          `'shape' prop is not supported on this theme, please use variant instead`,
        );
      }
      if (props.size != null) {
        throw new Error(
          `'size' prop is not supported on this theme, please use variant instead`,
        );
      }
      // If `bc` is set, use it as `variant` and remove it from props.
      // Compatibility layer so that downstream consumers with support for multiple
      // themes (e.g checkout) will continue to render buttons as expected.
      if (props.bc != null) {
        props.variant = props.bc;
        props.bc = undefined;
      }
    } else {
      defaults = {
        ...defaults,
        shape: 'other',
        size: 'default',
        palette: 'default',
      };

      // Merge props with button combination props from theme.
      // Props set explicitly on the component are given higher priority.
      if (buttonTheme != null && props.bc != null) {
        props = {
          ...buttonTheme.combinations[props.bc],
          ...props,
        };
      }
    }

    // Fill in default props
    props = {
      ...defaults,
      ...props,
    };

    const { shape, size, variant, disabledVariant, sx, type, ...rest } = props;
    const { disabled, href } = rest;

    // Render `<a/>` if href is in props
    let { as } = props;
    if (as == null) {
      as = href != null ? 'a' : undefined;
    }

    return (
      <SystemButton
        ref={ref}
        as={as}
        type={type != null ? type : as != null ? undefined : 'button'}
        textDecoration={href != null ? 'none' : undefined}
        __buttonCommon__="common"
        variant={variant}
        disabledVariant={disabled ? disabledVariant : undefined}
        __buttonSquareCommon__={shape === 'square' ? 'common' : undefined}
        __buttonSquareSize__={shape === 'square' ? size : undefined}
        __buttonRoundedCommon__={shape === 'rounded' ? 'common' : undefined}
        __buttonRoundedSize__={shape === 'rounded' ? size : undefined}
        __buttonRoundCommon__={shape === 'round' ? 'common' : undefined}
        __buttonRoundSize__={shape === 'round' ? size : undefined}
        __buttonOtherCommon__={shape === 'other' ? 'common' : undefined}
        __buttonOtherSize__={shape === 'other' ? size : undefined}
        sx={{
          ...sx,
          cursor: disabled ? 'not-allowed' : undefined,
        }}
        {...rest}
      />
    );
  },
);

export default Button;
